/**
 * You, and the way between the apps.
 *
 * TWO JOBS IN ONE PANEL, and they belong together. The products are separate
 * apps at separate hostnames now, which is good for focus and leaves a real
 * question: once you are inside News Beats, how do you get to Boardroom? A
 * nav that only shows the product you are in has no answer, and going back to
 * the launcher by typing a URL is not an answer either. So the account panel is
 * also the APP SWITCHER — the standard shape for this, and the only place in a
 * product shell that knows the other products exist.
 *
 * A DRAWER, NOT A PAGE. `/account` was a full screen you navigated INTO, which
 * meant leaving whatever you were doing and finding your way back — the trap
 * being that the page has no natural exit, so people use the browser Back
 * button and lose their place. A drawer overlays and dismisses: Escape, the
 * scrim, or the close button, and you are exactly where you were.
 *
 * The heavy account work — changing a password, the resume preference — stays on
 * `/account`. This is the switcher and the shortcuts, not a second copy of it.
 */
import { Link } from 'react-router-dom';
import Drawer from './common/Drawer';
import { withReturnTo } from './common/returnPath';
import { useAuth } from '../contexts/AuthContext';
import { useEntitlements } from '../contexts/EntitlementContext';
import { PRODUCT_INFO, PRODUCT_ORDER, shellUrl } from '../shell/products';
import { SHELLS, type Shell } from '../shell/shell';
import type { Product } from '../types';

export default function AccountDrawer({
  shell,
  onClose,
  returnLabel,
}: {
  shell: Shell;
  onClose: () => void;
  /**
   * What to call the page they are leaving.
   *
   * Passed in rather than worked out here: the nav sections that know a route's
   * name live in `Layout`, and importing them would drag a routed component
   * into a drawer that only needs one string. Falls back to the app's name for
   * routes outside the nav — Settings, the account page — which is what this
   * used to say for every route.
   */
  returnLabel?: string;
}) {
  const { user, logout } = useAuth();
  const { held, available } = useEntitlements();

  // Where they are now, so Account can offer a way back to it.
  const currentPath = window.location.pathname + window.location.search;
  const backLabel =
    returnLabel || (SHELLS[shell].product ? SHELLS[shell].short : 'home');
  const hostname = window.location.hostname;
  const origin = window.location.origin;

  // Catalog order, so the switcher does not reshuffle as entitlements load.
  const catalog: Product[] = available.length ? available : PRODUCT_ORDER;
  // AND ONE THIS BUNDLE CAN NAME. `available` is whatever the server lists, so a
  // product retired out of `PRODUCT_INFO` but still held — or one shipped
  // server-first — reached `info.name` as undefined and took the whole drawer
  // down, which is the panel you get to the OTHER apps from. Launcher and
  // Billing both skip an unknown key; this is the third place that has to.
  const mine = catalog.filter((p) => held.includes(p) && PRODUCT_INFO[p]);

  return (
    <Drawer onClose={onClose} width="w-[22rem]" eyebrow={user?.email || 'Signed in'}
      title={user?.username || 'Account'}>
      <div className="p-4 space-y-6">
        {mine.length > 0 && (
          <section>
            <h3 className="text-[11px] font-semibold tracking-wider uppercase text-gray-400 dark:text-gray-500 mb-2">
              Your apps
            </h3>
            <ul className="space-y-0.5">
              {mine.map((product) => {
                const info = PRODUCT_INFO[product];
                const here = SHELLS[shell].product === product;
                return (
                  <li key={product}>
                    {/* The one you are in is stated, not linked — a link back to
                        where you already are is a dead end that looks live. */}
                    {here ? (
                      <span className="flex items-center justify-between px-2 py-1.5 rounded-md text-sm bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-gray-100 font-medium">
                        {info.name}
                        <span className="text-[10px] uppercase tracking-widest opacity-50">
                          Here
                        </span>
                      </span>
                    ) : (
                      <a
                        href={shellUrl(info.shell, hostname, origin)}
                        className="block px-2 py-1.5 rounded-md text-sm text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
                      >
                        {info.name}
                      </a>
                    )}
                  </li>
                );
              })}
            </ul>
          </section>
        )}

        <section>
          <ul className="space-y-0.5 text-sm">
            {shell !== 'app' && (
              <li>
                {/* Back to the house. Named for what is there rather than
                    "Lumnian", which from inside a product reads as a brand
                    rather than a destination. */}
                <a
                  href={shellUrl('app', hostname, origin)}
                  className="block px-2 py-1.5 rounded-md text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
                >
                  All apps
                </a>
              </li>
            )}
            <li>
              {/* Credits and plans live in the house, whichever shell you are
                  in — one balance, one place to see it. */}
              <a
                href={`${shellUrl('app', hostname, origin)}/billing`}
                className="block px-2 py-1.5 rounded-md text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                Credits &amp; billing
              </a>
            </li>
            <li>
              <Link
                to={withReturnTo('/artifacts', currentPath, backLabel)}
                onClick={onClose}
                className="block px-2 py-1.5 rounded-md text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                Artifacts
              </Link>
            </li>
            <li>
              {/* Settings renders on WHATEVER host you are on — it is one
                  component whose tabs are scoped to what you hold, so there is
                  no reason to bounce anyone to another app to change a model. */}
              <Link
                to={withReturnTo('/settings', currentPath, backLabel)}
                onClick={onClose}
                className="block px-2 py-1.5 rounded-md text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                Settings
              </Link>
            </li>
            <li>
              {/* STAMP WHERE THEY CAME FROM. The drawer closes behind them, so
                  without a trail Account is a screen with no exit — and Back
                  stops working the moment they change a field. `withReturnTo`
                  is the app's existing mechanism for exactly this. */}
              <Link
                to={withReturnTo('/account', currentPath, backLabel)}
                onClick={onClose}
                className="block px-2 py-1.5 rounded-md text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                Account settings
              </Link>
            </li>
          </ul>
        </section>

        <section className="pt-4 border-t border-gray-200 dark:border-gray-700">
          <button
            type="button"
            onClick={async () => {
              await logout();
              window.location.href = '/login';
            }}
            className="w-full text-left px-2 py-1.5 rounded-md text-sm text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
          >
            Sign out
          </button>
        </section>
      </div>
    </Drawer>
  );
}
